import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import type { MarketCategory, MarketKind } from '@/types/market';
import styles from './Market.module.scss';

export default function MarketPost() {
  const navigate = useNavigate();

  const [kind, setKind] = useState<MarketKind>('for_sale');
  const [categories, setCategories] = useState<MarketCategory[]>([]);
  const [categoryCode, setCategoryCode] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('AED');
  const [isNegotiable, setIsNegotiable] = useState(false);
  const [city, setCity] = useState('');
  const [area, setArea] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [video, setVideo] = useState<string | null>(null);

  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        const cats = await apiService.mktGetCategories(kind);
        if (!mounted.current) return;
        setCategories(cats);
        // Reset category selection if it no longer belongs to this kind.
        setCategoryCode((prev) => (cats.some((c) => c.code === prev) ? prev : ''));
      } catch { /* non-fatal */ }
    })();
    return () => { mounted.current = false; };
  }, [kind]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const urls: string[] = [];
      for (const file of Array.from(files)) {
        const url = await apiService.mktUploadPhoto(file);
        if (url) urls.push(url);
      }
      if (mounted.current) setPhotos((prev) => [...prev, ...urls]);
    } catch (e: any) {
      if (mounted.current) setError(e?.message || 'Photo upload failed.');
    } finally {
      if (mounted.current) setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removePhoto = (url: string) => setPhotos((prev) => prev.filter((p) => p !== url));

  const handleVideo = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    // ~200MB cap matches the server limit; give a friendly message rather than a failed upload.
    if (file.size > 200 * 1024 * 1024) {
      setError('Video is too large. Please keep it under 200MB.');
      if (videoRef.current) videoRef.current.value = '';
      return;
    }
    setUploadingVideo(true);
    setError(null);
    try {
      const { url } = await apiService.mktUploadMedia(file);
      if (mounted.current && url) setVideo(url);
    } catch (e: any) {
      if (mounted.current) setError(e?.message || 'Video upload failed.');
    } finally {
      if (mounted.current) setUploadingVideo(false);
      if (videoRef.current) videoRef.current.value = '';
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!title.trim()) { setError('Please enter a title.'); return; }
    if (!categoryCode) { setError('Please choose a category.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const priceNum = price.trim() === '' ? null : Number(price);
      if (priceNum !== null && Number.isNaN(priceNum)) {
        setError('Price must be a number, or leave it blank for “Contact for price”.');
        setSubmitting(false);
        return;
      }
      const listing = await apiService.mktCreateListing({
        kind,
        categoryCode,
        title: title.trim(),
        description: description.trim() || undefined,
        price: priceNum,
        currency: priceNum !== null ? currency : undefined,
        city: city.trim() || undefined,
        area: area.trim() || undefined,
        isNegotiable,
        photos: photos.length ? photos : undefined,
        video: video || undefined,
      });
      navigate(`/customer/market/listing/${listing.id}`);
    } catch (e: any) {
      if (mounted.current) { setError(e?.message || 'Failed to post your ad.'); setSubmitting(false); }
    }
  };

  return (
    <>
      <DashboardHeader />
      <div className={styles.container}>
        <div className={styles.topBar}>
          <button type="button" className={styles.backButton} onClick={() => navigate(-1)}>← Back</button>
        </div>
        <h1 className={styles.title}>Post an ad</h1>

        <form onSubmit={submit}>
          {/* Kind */}
          <div className={styles.segment}>
            <button type="button" className={`${styles.segmentBtn} ${kind === 'for_sale' ? styles.segmentBtnActive : ''}`}
              onClick={() => setKind('for_sale')}>For sale</button>
            <button type="button" className={`${styles.segmentBtn} ${kind === 'service' ? styles.segmentBtnActive : ''}`}
              onClick={() => setKind('service')}>Service</button>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Category *</label>
            <select className={styles.select} value={categoryCode} onChange={(e) => setCategoryCode(e.target.value)} required>
              <option value="">Select a category…</option>
              {categories.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Title *</label>
            <input className={styles.input} value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder={kind === 'service' ? 'e.g. Home AC servicing' : 'e.g. iPhone 13 Pro, 256GB'} maxLength={120} required />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Description</label>
            <textarea className={styles.textarea} value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your item or service…" />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Price</label>
            <div className={styles.searchRow}>
              <select className={styles.select} value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ width: 100, flex: '0 0 auto' }}>
                <option value="AED">AED</option>
                <option value="USD">USD</option>
                <option value="INR">INR</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
              <input className={styles.input} type="number" min="0" step="0.01" value={price}
                onChange={(e) => setPrice(e.target.value)} placeholder="Leave blank for “Contact for price”" style={{ flex: 1 }} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 14, color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={isNegotiable} onChange={(e) => setIsNegotiable(e.target.checked)} />
              Price is negotiable
            </label>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Location</label>
            <div className={styles.searchRow}>
              <input className={styles.input} value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" style={{ flex: 1 }} />
              <input className={styles.input} value={area} onChange={(e) => setArea(e.target.value)} placeholder="Area (optional)" style={{ flex: 1 }} />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Photos</label>
            <div className={styles.uploadRow}>
              {photos.map((p) => (
                <div key={p} className={styles.uploadThumb}>
                  <img src={p} alt="upload" />
                  <button type="button" className={styles.uploadRemove} onClick={() => removePhoto(p)} aria-label="Remove photo">×</button>
                </div>
              ))}
              <div className={styles.uploadBox} onClick={() => fileRef.current?.click()}>
                {uploading ? <span className={styles.spinner} /> : '+'}
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => handleFiles(e.target.files)} />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Video (optional)</label>
            {video ? (
              <div className={styles.uploadRow}>
                <div className={styles.uploadThumb} style={{ width: 160, height: 90 }}>
                  <video src={video} controls playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                  <button type="button" className={styles.uploadRemove} onClick={() => setVideo(null)} aria-label="Remove video">×</button>
                </div>
              </div>
            ) : (
              <div className={styles.uploadRow}>
                <div className={styles.uploadBox} style={{ width: 160 }} onClick={() => !uploadingVideo && videoRef.current?.click()}>
                  {uploadingVideo ? <span className={styles.spinner} /> : '+ Add video'}
                </div>
              </div>
            )}
            <p className={styles.muted} style={{ fontSize: 12, marginTop: 4 }}>One short clip, up to 200MB.</p>
            <input ref={videoRef} type="file" accept="video/*" hidden onChange={(e) => handleVideo(e.target.files)} />
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <button type="submit" className={styles.primaryBtn} disabled={submitting || uploading || uploadingVideo} style={{ width: '100%', marginTop: 8 }}>
            {submitting ? 'Posting…' : 'Post ad'}
          </button>
        </form>
      </div>
      <Footer />
    </>
  );
}
